import React, { useState, useEffect } from "react";
import Delete from '@mui/icons-material/Delete';
import LinkedIn from '@mui/icons-material/LinkedIn';
import RemoveRedEye from '@mui/icons-material/RemoveRedEye';
import {
  Avatar, Chip, IconButton,
  List, ListItem, ListItemAvatar, ListItemText,
  Popover, useMediaQuery, Box, Tooltip, Typography
} from "@mui/material";

import { useHistory, Link } from "react-router-dom";
import { getUserData, getSenioriteFromDate } from "../../../../utils/FunctionHelpers";
import { FiArchive } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { SetSelectedCandidatBeAddToSelection } from "../../../../redux/candidats/actions/candidatsActions";
import { displaySwipeableDrawerSummaryCandidat } from "../../../../redux/SwipeableDrawer/actions/swipeableDrawerActions";
import { v4 } from "uuid";
import { deleteCandidat } from "../../../../services/CandidatService";
import DialogDeleteCustom from "../../../../components/DialogDeleteCustom/DialogDeleteCustom";
import { withSnackbar } from "../../../../utils/SnackbarHOC";
import { handleOnViewColumnsChange, displayColumn } from "../../../../utils/viewColumns";
import { handleChangeRowsPerPage, getRowsPerPage } from "../../../../utils/DataTables";
import moment from "moment";
import ShowAddCvCandidat from "../../VisualiserCandidat/ShowAddCvCandidat";
import { debounce } from "lodash";
import { commonGridSx, gridContainerSx, gridLocaleText, makeGridToolbar } from "../../../../utils/gridStyles";
import { getSelectedRowIds } from "../../../../utils/gridSelection";
import { DataGrid } from '../../../../components/SortableDataGrid/SortableDataGrid';

const tableName = "candidatColumnVisibility";

const CustomToolbar = makeGridToolbar('Candidats');

const CandidatTable = ({
  candidatsData = {
    candidats: [],
    candidatsCount: 0,
    limit: 50,
    pageCandidat: 1,
    sort: { column: "", direction: "asc" },
    isLoading: false,
  },
  handleSearchCandidats = () => {},
  parameters = {},
  snackbarShowMessage = () => {},
  aiMode = false,
  aiRows = { data: [], isLoading: false },
  onImportCandidats = null,
}) => {
  const [candidats, setCandidats] = useState([]);
  const dispatch = useDispatch();
  const history = useHistory();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.between(0, 767));
  const isMediumMode = useMediaQuery((theme) => theme.breakpoints.between(767, 1023));

  const [loadToDelete, setLoadToDelete] = useState(false);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [candidatSelectedToDelete, setCandidatSelectedToDelete] = useState(null);

  const [paginationModel, setPaginationModel] = useState({
    page: candidatsData.pageCandidat - 1,
    pageSize: getRowsPerPage(tableName) ?? candidatsData.limit ?? 50,
  });

  const [sortModel, setSortModel] = useState(
    candidatsData.sort?.column
      ? [{ field: candidatsData.sort.column, sort: candidatsData.sort.direction }]
      : []
  );

  const buildVisibility = () => ({
    numCandidat: displayColumn("numCandidat", tableName) !== false,
    updatedAt: displayColumn("updatedAt", tableName) !== false,
    ia_score: false,
    nom: displayColumn("nom", tableName) !== false,
    prenom: displayColumn("prenom", tableName) !== false,
    prequalifClientRhAt: displayColumn("prequalifClientRhAt", tableName) !== false,
    mailSentAt: displayColumn("mailSentAt", tableName) !== false,
    testTechniqueAt: displayColumn("testTechniqueAt", tableName) !== false,
    id: false,
    societe: displayColumn("societe", tableName) !== false,
    societeId: false,
    telephone: displayColumn("telephone", tableName) !== false,
    email: displayColumn("email", tableName) !== false,
    fonction: displayColumn("fonction", tableName) !== false,
    dateDebutExperience: displayColumn("dateDebutExperience", tableName) !== false,
    preAvis: displayColumn("preAvis", tableName) !== false,
    localisation: displayColumn("localisation", tableName) !== false,
    diplome: displayColumn("diplome", tableName) !== false,
    linkedin: displayColumn("linkedin", tableName) !== false,
    sourceur: displayColumn("sourceur", tableName) !== false,
    consultant: displayColumn("consultant", tableName) !== false,
    manager: displayColumn("manager", tableName) !== false,
    selections: displayColumn("selections", tableName) !== false,
    ia_method: false,
    actions: true,
    uploadedFiles: false,
  });

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(buildVisibility);

  const handleDeleteCandidat = async (uuid) => {
    try {
      setLoadToDelete(true);
      snackbarShowMessage("Patientez svp...", "warning", null);
      const data = await deleteCandidat(uuid);
      handleSearchCandidats(candidatsData.pageCandidat, parameters);
      snackbarShowMessage(data);
    } catch {
      snackbarShowMessage("Erreur à la suppression du candidat", "error", 4000);
    } finally {
      setLoadToDelete(false);
      setOpenDialogDelete(false);
    }
  };

  const handlePopoverOpen = (event, id) => {
    setCandidats((prev) => prev.map((c) => (c.id === id ? { ...c, anchorEl: event.currentTarget } : c)));
  };

  const handlePopoverClose = (id) => {
    setCandidats((prev) => prev.map((c) => (c.id === id ? { ...c, anchorEl: null } : c)));
  };

  const handleOpenDialog = (candidat) => {
    setCandidatSelectedToDelete({
      id: candidat.id,
      fullName: candidat.fullName.charAt(0).toUpperCase() + candidat.fullName.slice(1),
    });
    setOpenDialogDelete(true);
  };

  const handleCloseDialog = () => {
    if (loadToDelete) return;
    setOpenDialogDelete(false);
  };

  const debouncedSearch = debounce((searchText, handleSearch, params, page, pageSize, column, sort) => {
    handleSearch(page + 1, { ...params, column, sort, limit: pageSize, fonction: searchText });
  }, 800);

  const handlePaginationModelChange = (newModel) => {
    if (newModel.pageSize !== paginationModel.pageSize) {
      handleChangeRowsPerPage(tableName, newModel.pageSize);
      handleSearchCandidats(1, parameters, newModel.pageSize);
    } else {
      handleSearchCandidats(newModel.page + 1, {
        ...parameters,
        column: sortModel[0]?.field || '',
        sort: sortModel[0]?.sort || 'asc',
        limit: newModel.pageSize,
      });
    }
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    handleSearchCandidats(paginationModel.page + 1, {
      ...parameters,
      column: newSortModel[0]?.field || '',
      sort: newSortModel[0]?.sort || 'asc',
      limit: paginationModel.pageSize,
    });
  };

  const handleColumnVisibilityModelChange = (newModel) => {
    Object.entries(newModel).forEach(([field, visible]) => {
      if (columnVisibilityModel[field] !== visible) {
        handleOnViewColumnsChange(field, visible ? "add" : "remove", tableName);
      }
    });
    setColumnVisibilityModel(newModel);
  };

  const handleFilterModelChange = (filterModel) => {
    const searchText = filterModel.quickFilterValues?.join(' ') || '';
    debouncedSearch(
      searchText,
      handleSearchCandidats,
      parameters,
      paginationModel.page,
      paginationModel.pageSize,
      sortModel[0]?.field || '',
      sortModel[0]?.sort || 'asc',
    );
  };

  const resolveCandidateId = (row) =>
    row?.id ?? row?.uuid ?? row?.candidatId ?? row?.candidat_id ?? row?.candidateId ?? row?.candidatUuid ?? row?.candidat_uuid ?? null;

  const toJsDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v === "string") return new Date(v);
    if (v && typeof v === "object" && v.date) return new Date(v.date);
    return null;
  };

  useEffect(() => {
    const sourceRows = aiMode ? (aiRows.data || []) : (candidatsData.candidats || []);
    setCandidats(sourceRows.map((c) => ({ ...c, anchorEl: null })));
  }, [aiMode, aiRows.data, candidatsData.candidats]);

  useEffect(() => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      ia_score: aiMode ? (displayColumn("ia_score", tableName) !== false) : false,
    }));
  }, [aiMode]);

  const storeNavigationState = (candidatId, tabIndex) => {
    const candidatsIds = candidats.map((c) => c.id);
    localStorage.setItem('viewCandidatState', JSON.stringify({
      tabIndex,
      candidatsInList: candidatsIds,
      candidatIndex: candidatsIds.indexOf(candidatId),
      numbersTotalOfSelections: candidats.length,
    }));
  };

  const viewCandidat = (candidatId, tabIndex) => {
    const candidatsIds = candidats.map((c) => c.id);
    storeNavigationState(candidatId, tabIndex);
    history.push({
      pathname: `/candidats/${candidatId}/view`,
      state: {
        tabIndex,
        candidatsInList: candidatsIds,
        candidatIndex: candidatsIds.indexOf(candidatId),
        numbersTotalOfSelections: candidats.length,
      },
    });
  };

  const previewCandidat = (event, candidatId) => {
    event?.preventDefault();
    event?.stopPropagation();
    dispatch(displaySwipeableDrawerSummaryCandidat({ show: true, id: candidatId }));
  };

  const PopoverComponent = ({ selections, candidat }) => {
    const anchorEl = candidat?.anchorEl || null;
    return (
      <Popover
        sx={{ pointerEvents: 'none', '& .MuiPaper-root': { p: 1 } }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClose={() => handlePopoverClose(candidat?.id)}
        disableRestoreFocus
      >
        <List dense sx={{ minWidth: 160 }}>
          {selections?.map((selection) => (
            <ListItem key={v4()} disableGutters sx={{ px: 1.5 }}>
              <ListItemAvatar sx={{ minWidth: 32 }}>
                <Avatar sx={{ width: 24, height: 24, bgcolor: '#ebf4fb' }}>
                  <FiArchive size={12} color="#03407b" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={selection?.nom}
                primaryTypographyProps={{ fontSize: '1rem', color: '#334155' }}
              />
            </ListItem>
          ))}
        </List>
      </Popover>
    );
  };

  const dataIa = (aiRows.data || []).map((r) => ({
    ...r,
    id: resolveCandidateId(r),
    selections: Array.isArray(r?.selections) ? r.selections : [],
    updatedAt: r?.updatedAt?.date ? moment(r.updatedAt.date).toDate().getTime() : r?.updatedAt ?? null,
    anchorEl: null,
  }));

  const rows = (aiMode && (dataIa.length > 0 || aiRows.isLoading))
    ? dataIa
    : candidats.map((item) => ({
        ...item,
        updatedAt: item.updatedAt?.date ? moment(item.updatedAt.date).toDate().getTime() : null,
      }));

  const isLoading = aiMode ? aiRows.isLoading : candidatsData.isLoading;

  const gridColumns = [
    {
      field: 'numCandidat',
      headerName: 'N° CANDIDAT',
      width: 140,
      renderCell: (params) => (
        <ShowAddCvCandidat
          snackbarShowMessage={snackbarShowMessage}
          files={params.row.uploadedFiles || []}
          fileName={`${params.row.nom} ${params.row.prenom}`}
        >
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#03407b', cursor: 'pointer' }}>
            {params.value}
          </Typography>
        </ShowAddCvCandidat>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'DATE MAJ',
      width: 110,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
          {params.value != null ? moment(params.value).format("DD/MM/YYYY") : "—"}
        </Typography>
      ),
    },
    {
      field: 'ia_score',
      headerName: 'SCORE',
      width: 90,
      // The score only exists in AI mode, where sorting is done client-side
      sortable: aiMode,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        params.value != null
          ? <Chip label={Number(params.value).toFixed(2)} size="small" sx={{ background: '#ebf4fb', color: '#03407b', fontWeight: 600, fontSize: '1rem' }} />
          : <Typography sx={{ color: '#cbd5e1', fontSize: '1rem' }}>—</Typography>
      ),
    },
    {
      field: 'nom',
      headerName: 'NOM',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 140 }} title={params.value}>
          <Link
            style={{ textDecoration: 'none', fontWeight: 600, color: '#0f172a', fontSize: '1rem', textTransform: 'uppercase' }}
            to={{ pathname: `/candidats/${params.row.id}/view` }}
            onContextMenu={() => storeNavigationState(params.row.id, 0)}
            onClick={(e) => { e.preventDefault(); viewCandidat(params.row.id, 0); }}
          >
            {params.value}
          </Link>
        </Box>
      ),
    },
    {
      field: 'prenom',
      headerName: 'PRÉNOM',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 140 }} title={params.value}>
          {!(isSmall || isMediumMode) && (
            <Tooltip title="Voir le profil">
              <IconButton
                size="small"
                onClick={(e) => previewCandidat(e, params.row.id)}
                onContextMenu={() => storeNavigationState(params.row.id, 0)}
                sx={{ color: '#94a3b8', p: 0.25, '&:hover': { color: '#03407b', background: '#ebf4fb' }, borderRadius: 1, flexShrink: 0 }}
              >
                <RemoveRedEye sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          <Link
            style={{ textDecoration: 'none', color: '#334155', fontSize: '1rem', textTransform: 'capitalize' }}
            to={{ pathname: `/candidats/${params.row.id}/view` }}
            onContextMenu={() => storeNavigationState(params.row.id, 0)}
            onClick={(e) => { e.preventDefault(); viewCandidat(params.row.id, 0); }}
          >
            {params.value}
          </Link>
        </Box>
      ),
    },
    {
      field: 'prequalifClientRhAt',
      headerName: 'PRÉQUALIF RH',
      width: 130,
      renderCell: (params) => {
        const d = toJsDate(params.value);
        return <Typography sx={{ fontSize: '1rem', color: d ? '#334155' : '#cbd5e1' }}>{d ? moment(d).format("DD/MM/YYYY") : "—"}</Typography>;
      },
    },
    {
      field: 'mailSentAt',
      headerName: 'MAIL (ENVOI)',
      width: 125,
      renderCell: (params) => {
        const d = toJsDate(params.value);
        return <Typography sx={{ fontSize: '1rem', color: d ? '#334155' : '#cbd5e1' }}>{d ? moment(d).format("DD/MM/YYYY") : "—"}</Typography>;
      },
    },
    {
      field: 'testTechniqueAt',
      headerName: 'TEST TECHNIQUE',
      width: 145,
      renderCell: (params) => {
        const d = toJsDate(params.value);
        return <Typography sx={{ fontSize: '1rem', color: d ? '#334155' : '#cbd5e1' }}>{d ? moment(d).format("DD/MM/YYYY") : "—"}</Typography>;
      },
    },
    {
      field: 'id',
      headerName: 'N°',
      sortable: false,
      width: 80,
      renderCell: (params) => (
        <Box title={params.value}>{params.value?.substring(0, 30).replace(/\D/g, "").substring(1, 7)}</Box>
      ),
    },
    {
      field: 'societe',
      headerName: 'SOCIÉTÉ',
      width: 130,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 120 }} title={params.value}>
          <Link
            style={{ textDecoration: 'none', color: '#0ea5e9', fontWeight: 500, fontSize: '1rem' }}
            to={{ pathname: `/societe/${params.row.societeId}/view` }}
          >
            {params.value}
          </Link>
        </Box>
      ),
    },
    {
      field: 'societeId',
      headerName: 'N° SOCIÉTÉ',
      sortable: false,
      width: 100,
    },
    {
      field: 'telephone',
      headerName: 'MOBILE',
      width: 130,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '1rem', color: '#334155', whiteSpace: 'nowrap' }}>{params.value || "—"}</Typography>
      ),
    },
    {
      field: 'email',
      headerName: 'EMAIL',
      width: 210,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: 200 }} title={params.value}>
          <Typography sx={{ fontSize: '1rem', color: '#334155' }}>{params.value || "—"}</Typography>
        </Box>
      ),
    },
    {
      field: 'fonction',
      headerName: 'FONCTION',
      width: 220,
      renderCell: (params) => (
        <Box sx={{ wordWrap: 'break-word', maxWidth: 210 }} title={params.value}>
          <Typography sx={{ fontSize: '1rem', color: '#475569', textTransform: 'capitalize' }}>{params.value || "—"}</Typography>
        </Box>
      ),
    },
    {
      field: 'dateDebutExperience',
      headerName: 'XP',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value === null ? "N/A" : getSenioriteFromDate(params.value?.date)}
          size="small"
          sx={{ fontSize: '1rem', height: 20, background: '#f1f5f9', color: '#475569' }}
        />
      ),
    },
    {
      field: 'preAvis',
      headerName: 'PRÉAVIS',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569' }}>
          {["null", "", null, undefined].includes(params.value) ? "—" : params.value}
        </Typography>
      ),
    },
    {
      field: 'localisation',
      headerName: 'LOCALISATION',
      width: 120,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{params.value ?? "—"}</Typography>
      ),
    },
    {
      field: 'diplome',
      headerName: 'DIPLÔME',
      width: 120,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{params.value ?? "—"}</Typography>
      ),
    },
    {
      field: 'linkedin',
      headerName: 'LKD',
      width: 60,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Voir le profil LinkedIn">
          <a href={params.value} target="_blank" rel="noreferrer" style={{ color: '#0a66c2', lineHeight: 0, display: 'flex', alignItems: 'center' }}>
            <LinkedIn sx={{ fontSize: 20 }} />
          </a>
        </Tooltip>
      ),
    },
    {
      field: 'sourceur',
      headerName: 'SOURCEUR',
      width: 130,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: 'consultant',
      headerName: 'TALENT MANAGER',
      width: 155,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{params.value || "—"}</Typography>
      ),
    },
    {
      field: 'manager',
      headerName: 'DELIVERY MANAGER',
      width: 165,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{params.value || "—"}</Typography>
      ),
    },
    {
      field: 'selections',
      headerName: 'VIVIERS',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      sortComparator: (a, b) => ((a?.length || 0) - (b?.length || 0)),
      renderCell: (params) => {
        const selections = Array.isArray(params.value) ? params.value : [];
        const count = selections.length;
        const candidat = candidats.find((c) => c.id === params.row.id) || params.row;
        return count === 0 ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
            onMouseEnter={(e) => handlePopoverOpen(e, params.row.id)}
            onMouseLeave={() => handlePopoverClose(params.row.id)}
          >
            <Typography sx={{ fontSize: '1rem', color: '#cbd5e1' }}>0</Typography>
          </Box>
        ) : (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', width: '100%', cursor: 'pointer' }}
            onMouseEnter={(e) => handlePopoverOpen(e, params.row.id)}
            onMouseLeave={() => handlePopoverClose(params.row.id)}
            onClick={(e) => { e.preventDefault(); viewCandidat(params.row.id, 3); }}
            onContextMenu={() => storeNavigationState(params.row.id, 3)}
          >
            <Chip
              label={count}
              size="small"
              sx={{ background: '#ebf4fb', color: '#03407b', fontWeight: 600, fontSize: '0.8125rem', cursor: 'pointer' }}
            />
            <PopoverComponent selections={selections} candidat={candidat} />
          </Box>
        );
      },
    },
    {
      field: 'ia_method',
      headerName: 'MÉTHODE',
      sortable: aiMode,
      width: 110,
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Tooltip title="Voir le profil">
            <IconButton
              size="small"
              onClick={(e) => previewCandidat(e, params.row.id)}
              sx={{ color: '#03407b', background: '#ebf4fb', borderRadius: '8px', p: 0.75, '&:hover': { background: '#d6eaf8' } }}
            >
              <RemoveRedEye sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ajouter à une sélection">
            <IconButton
              size="small"
              onClick={() => dispatch(SetSelectedCandidatBeAddToSelection([params.row.id]))}
              sx={{ color: '#10b981', background: '#f0fdf4', borderRadius: '8px', p: 0.75, '&:hover': { background: '#dcfce7' } }}
            >
              <FiArchive size={14} />
            </IconButton>
          </Tooltip>
          {getUserData()?.role?.includes('ROLE_DIRECTION') && (
            <Tooltip title="Supprimer">
              <IconButton
                size="small"
                onClick={() => handleOpenDialog({ id: params.row.id, fullName: `${params.row.nom} ${params.row.prenom}` })}
                sx={{ color: '#ef4444', background: '#fef2f2', borderRadius: '8px', p: 0.75, '&:hover': { background: '#fee2e2' } }}
              >
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'uploadedFiles',
      headerName: 'Files',
      width: 0,
      sortable: false,
    },
  ];

  return (
    <>
      <Box sx={{ ...gridContainerSx, height: 'calc(100vh - 280px)', width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={gridColumns}
          loading={isLoading}
          getRowId={(row) => row.id}
          paginationMode={aiMode ? "client" : "server"}
          sortingMode={aiMode ? "client" : "server"}
          filterMode={aiMode ? "client" : "server"}
          rowCount={aiMode ? rows.length : (candidatsData.candidatsCount ?? 0)}
          paginationModel={paginationModel}
          onPaginationModelChange={aiMode ? setPaginationModel : handlePaginationModelChange}
          pageSizeOptions={[50, 100]}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onFilterModelChange={aiMode ? undefined : handleFilterModelChange}
          checkboxSelection={!aiMode}
          disableRowSelectionOnClick
          onRowSelectionModelChange={(model) => {
            if (!aiMode) dispatch(SetSelectedCandidatBeAddToSelection(getSelectedRowIds(model, rows)));
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={{
            ...gridLocaleText,
            noRowsLabel: aiMode ? "Aucun résultat IA" : "Aucun enregistrement trouvé.",
          }}
          sx={commonGridSx}
        />
      </Box>

      {!aiMode && (
        <DialogDeleteCustom
          handleClose={handleCloseDialog}
          open={openDialogDelete}
          loading={loadToDelete}
          title="Supprimer le candidat"
          handleConfirm={() => handleDeleteCandidat(candidatSelectedToDelete?.id)}
          label={`Voulez-vous vraiment supprimer le candidat ${candidatSelectedToDelete?.fullName} ?`}
        />
      )}
    </>
  );
};

export default withSnackbar(CandidatTable);
